import {Brand} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/brand.types';
import {BrandPreview} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/brand_preview.types';
import {
  GetHeaderResponse_SubBrands,
  GetHeaderV2Response_SubBrands,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/brand_hub/v1/service.types';
import {LocationType, useHistory, useIsMultiBrand} from '@aboutyou/router';
import {useCallback} from 'react';
import {FIRST_PRODUCT_ID_PARAM} from '../../../../../features/firstProductId/useFirstProductIds';
import {BrandPreviewEntity} from '../../../utils/isBrandPreviewEntities';
import {useBrandShopIdentifier} from '../../../utils/useBrandShopIdentifier';

export const useActiveSubBrand = ({
  subBrands,
  parentBrand,
}: {
  subBrands?: GetHeaderResponse_SubBrands | GetHeaderV2Response_SubBrands;
  parentBrand?: Brand;
}) => {
  const history = useHistory();

  const isMultiBrand = useIsMultiBrand();
  const {id: activeBrandId} = useBrandShopIdentifier();
  const activeBrandSliderIndex =
    isMultiBrand || !subBrands
      ? 0
      : subBrands.subBrands.findIndex(
          brand => brand.brand.id === activeBrandId,
        );

  const getBrandRedirectUrl = useCallback(
    ({brand, brandPreview}: {brand: Brand; brandPreview?: BrandPreview}) => {
      const {id: brandId, path} = brand;
      const queryParams = new URLSearchParams(history.location.search);
      queryParams.delete(FIRST_PRODUCT_ID_PARAM);

      if (!parentBrand) {
        return {};
      }

      const targetUrl =
        activeBrandId !== brandId || isMultiBrand
          ? path
          : `${parentBrand.path}/all`;

      return {
        pathname: targetUrl,
        search: queryParams.toString(),
        ...(brandPreview && {
          state: {
            entities: {brandPreview} as BrandPreviewEntity,
          },
        }),
      } as LocationType;
    },
    [activeBrandId, history.location.search, isMultiBrand, parentBrand],
  );

  const getBrandState = useCallback(
    (brandId: number) => {
      return isMultiBrand || activeBrandId !== brandId
        ? 'deselected'
        : 'selected';
    },
    [activeBrandId, isMultiBrand],
  );

  const getBrandImage = ({
    brand,
    brandPreview,
  }: {
    brand: Brand;
    brandPreview: BrandPreview;
  }) => {
    const brandWhiteImage = getWhiteImageIfExists(brandPreview);
    const brandBlackImage = brand.blackLogoImage;

    return {brandWhiteImage, brandBlackImage};
  };

  return {
    activeBrandSliderIndex,
    isMultiBrand,
    getBrandState,
    getBrandImage,
    getBrandRedirectUrl,
  };
};

const getWhiteImageIfExists = (brandPreview: BrandPreview) => {
  return brandPreview.type?.$case === 'images'
    ? brandPreview.type.images.whiteLogoImage
    : undefined;
};
